import { z } from "zod";
import {} from "./error.js";
/**
 * **The shop wire shape, frozen a wave ahead — architecture A121 Item 3
 * (T-398 / E1 `SHOP-SCHEMA`).**
 *
 * Types and schemas only. **No route in this file's wave** — the catalog and
 * inventory reads are E4, the purchase seam is E5, the storefront is E7. What is
 * declared here is exactly the part of the contract that would otherwise become
 * a **wire change on a money path** if it arrived later: the closed refusal
 * vocabulary, and the client `nonce`.
 *
 * ── EXTENDED by A121 addendum-2 (2026-08-15, T-402 / E5) ───────────────────
 *
 * Dex's addendum ratifying the nonce's lifetime named three additions, and this
 * file carries exactly those three and nothing else: `price_changed` in the
 * refusal union, `expectPriceCoins` on the request, and
 * {@link shopPurchaseSuccessSchema}'s `replayed` on the success envelope. All
 * three are unreachable in this era. He also ruled **against** a `nonce_replay`
 * code — a replayed nonce is a success replay, not a refusal — so a later reader
 * who expects to find one is looking at a decision rather than at a gap.
 *
 * ── Why the refusal union is declared in full while most of it is unreachable ─
 *
 * A121 fixes the vocabulary now so the pricing ticket
 * (`SHOP-DEBIT-PURCHASE`) is a **body swap** — it replaces the server's answer,
 * not the shape of the answer. A client written against this union today keeps
 * working when the branches appear, and every refusal it can ever receive is one
 * it was compiled against. The alternative — grow the enum with the feature —
 * makes each pricing-wave step a client release.
 *
 * ── What is NOT in this union, deliberately ─────────────────────────────────
 *
 * **Suspension.** LOCKED rule 41's `requirePlayer` refuses a suspended account
 * upstream of every shop handler, so a `suspended` refusal code here would be a
 * second, weaker copy of a gate that already answered (A81). If it ever appears
 * in this list, the gate moved and that is the thing to review.
 *
 * ── The fence this file lives inside ────────────────────────────────────────
 *
 * A121 Item 1's governing property is that the shop scaffold is **structurally
 * incapable of moving coins**: no shop module has a reachable path to the ledger
 * package or to a money table. This file is a shop module. It imports zod and
 * one sibling type, and `shop-ledger-fence.test.ts` is what keeps that true as
 * the wave grows.
 */
/**
 * The categories the client has an `ItemPreview` branch for — **a presentation
 * fact, and the only copy of it** (A81).
 *
 * `shop_items.category` is deliberately **unconstrained at the database**
 * (A121's T-398 addendum, D2: *constrain what protects money, never what
 * protects layout*), so adding a category stays "data plus at most one preview
 * branch". The cost of that freedom is that a typo produces a category nothing
 * can draw — and the ruled answer is that it **surfaces**: the catalog read logs
 * it and returns the item with `previewFallback: true`, never dropping, hiding
 * or crashing on it.
 *
 * This list is what "known" means for that marker, and E7's preview switch reads
 * the same list rather than restating it.
 */
export const SHOP_PREVIEW_CATEGORIES = ["avatar", "frame", "flair", "emote"];
/** Whether the client is expected to have a preview branch for this category. */
export function isKnownShopCategory(category) {
    return SHOP_PREVIEW_CATEGORIES.includes(category);
}
/**
 * Why a purchase was refused — the **complete** list, closed, one COPY-SLOT per
 * value (the same discipline as A73's identity-blocked reasons).
 *
 * In the scaffold exactly one of these is reachable: every request answers
 * `shop_unavailable`. The rest are declared, unreachable, and named so the seam's
 * purpose is legible.
 */
export const SHOP_REFUSAL_CODES = [
    /**
     * The scaffold's only outcome, **and** the flag-off outcome once the seam has
     * branches. A121: it is a *state* refusal — "this is not available right now"
     * — not an unimplemented server, which is why it answers 409 and never 501.
     */
    "shop_unavailable",
    /** No catalog row for the given id/sku. */
    "item_not_found",
    /** The row exists but its A102 lifecycle status is not `live`. */
    "item_not_live",
    /** The player already owns it and `max_per_player` is 1. */
    "already_owned",
    /** The player owns `max_per_player` of it. */
    "max_per_player_reached",
    /** **Pricing wave.** Declared now, unreachable in the scaffold. */
    "insufficient_funds",
    /**
     * **Pricing wave — A121 addendum-2 (3), T-402 / E5.** The catalog price moved
     * between the dialog rendering and the player confirming, so the server would
     * have charged something other than what was shown. It is the refusal half of
     * {@link shopPurchaseRequestSchema}'s `expectPriceCoins` compare-and-set —
     * the house pattern for money-bearing writes (A116 Item 7; the force-close
     * seam's `expect:{phase}`).
     *
     * Dex's addendum is explicit that this code *could* have waited — Ivy §6.4's
     * `shop.refuse.unknown` fallback is LOCKED, so an unrecognised code degrades
     * gracefully — while the **request field could not**. It is declared here
     * anyway so the union and the field arrive together and one reader never sees
     * half of the mechanism.
     *
     * **OWED, and not by this ticket:** one COPY-SLOT from Ivy/Theo
     * (`shop.refuse.price_changed`) at pricing time. Until then a client that
     * somehow received this code renders §6.4's fallback string, which is exactly
     * what that LOCKED fallback is for.
     */
    "price_changed",
];
export const shopRefusalCodeSchema = z.enum(SHOP_REFUSAL_CODES);
/**
 * The refusal body, carried in `details` of the api's existing error envelope
 * (`{ error: { code, message, details } }`) — the same construction as a refused
 * rejoin, and the reason no new envelope is invented here.
 *
 * The client renders `reason`; it never parses `message`, which is for
 * developers and logs.
 */
export const shopRefusalDetailsSchema = z.object({
    reason: shopRefusalCodeSchema,
});
/**
 * **A121, binding:** `shop_unavailable` answers **409**, not 501. Declared as a
 * value rather than left to the route so the seam ticket cannot pick a status
 * that says "not implemented" about a decision that is deliberate.
 */
export const SHOP_UNAVAILABLE_ERROR_CODE = "conflict";
/**
 * The purchase request body — **carrying the `nonce` from day one**.
 *
 * §5.3's LOCKED idempotency key for a purchase is
 * `shop:{playerId}:{itemId}:{nonce}`, so the nonce is the client's cart
 * submission id: the thing that makes a double-tap, a retry and a replayed
 * request land on the same key and charge once. Adding it later would be a wire
 * change on a money path; adding it now is free — which is the whole argument
 * for declaring this file a wave early.
 *
 * It is **carried but unused** in the scaffold: the seam refuses unconditionally,
 * so replay is trivially safe and the nonce becomes a real test at pricing.
 *
 * `itemId` **or** `sku`, exactly one. Both address the same row; the storefront
 * has ids from the catalog response, and an operator/test caller has skus.
 *
 * ── The nonce's lifetime — A121 addendum-2 (1), RATIFIED (T-402 / E5) ───────
 *
 * Generated **once when the dialog opens**, regenerated **only on re-open, never
 * per press**: Ivy §5.5 gives the player a retry after a transport failure, and
 * a request that timed out may still have committed — so a retry bearing a new
 * nonce would mint a second `shop:{playerId}:{itemId}:{nonce}` key and
 * double-debit at pricing time. Three consequences the pricing wave inherits,
 * recorded here so they are not re-derived:
 *
 * 1. The dedupe is **not a new mechanism** — it is §5.3 invariant 3 (the ledger's
 *    `idempotency_key UNIQUE`) reached through that package's existing
 *    single-transaction path. **A81: one home. No shop-specific dedupe, and no
 *    nonce table — now or at pricing** (one would also break A121's promise that
 *    pricing adds zero schema).
 * 2. A replayed nonce whose original committed returns the **same success**, with
 *    {@link shopPurchaseSuccessSchema}'s `replayed: true` — never an error. A
 *    replay that errored would send the player back to a re-opened dialog with a
 *    *fresh* nonce: a real double debit created by the mechanism meant to prevent
 *    one. That is why there is **no `nonce_replay` refusal code**.
 * 3. The nonce dedupes the transport retry, **not the player's second decision**.
 *    A re-opened dialog is a new attempt, refused (if at all) by `already_owned`.
 *    The nonce is a request key and **never an authorization**.
 *
 * **What E5 does with it today:** validates the shape, persists nothing, and does
 * not echo it back — correlation is the `requestId`, and the client already holds
 * its own nonce.
 */
export const shopPurchaseRequestSchema = z
    .object({
    itemId: z.string().uuid().optional(),
    sku: z.string().min(1).max(64).optional(),
    /**
     * Opaque to the server, and never interpreted — it is a discriminator, not
     * an identifier. Bounded because it becomes part of a stored key.
     */
    nonce: z.string().min(1).max(64),
    /**
     * **The price the player was shown — A121 addendum-2 (3), frozen now.**
     *
     * A compare-and-set on the money the request is about: at pricing time a
     * catalog price can move between the dialog rendering and the player
     * confirming, and charging more than was displayed is a money defect. The
     * server will compare this against the row it is about to charge and refuse
     * `price_changed` rather than charge a number nobody agreed to — the same
     * pattern every money-bearing write here already uses (A116 Item 7).
     *
     * **Required and nullable**, which is the whole point of freezing it early:
     * the scaffold's client sends `null` (nothing is priced), a pricing-era
     * client sends the number off the card, and the *key is present from day
     * one*. A refusal code can be added later — Ivy §6.4's `shop.refuse.unknown`
     * fallback is LOCKED — but a **request** field cannot: adding it at pricing
     * would be a wire change on a money path, exactly what carrying the nonce
     * early avoided.
     *
     * E5 validates it and branches on nothing: the compare has no price to
     * compare against while every row is unpriced.
     */
    expectPriceCoins: z.number().int().nullable(),
})
    .refine((body) => (body.itemId === undefined) !== (body.sku === undefined), {
    message: "exactly one of itemId or sku is required",
    path: ["itemId"],
});
/**
 * **The success envelope — declared, and unreachable in this era.**
 *
 * A121 addendum-2 (3), T-402 / E5. There is no code path in the scaffold that
 * can produce this value: the seam's handler is typed `Promise<never>`, the
 * purchase route registers no `2xx` response schema, and
 * `shop-purchase.db.test.ts` drives the whole request matrix without ever
 * observing a status below 400. It is declared here for the same reason
 * `insufficient_funds` is — so the pricing wave is a **body swap** rather than a
 * wire change, and so a client compiled against this file today already knows
 * the shape of the answer it will one day receive.
 *
 * ── Why `replayed`, and why it is the ONLY field frozen ────────────────────
 *
 * `replayed` is load-bearing on the money path and cannot be inferred by the
 * client: a transport retry carrying the same nonce (§5.6, ratified) must return
 * the **same success** the original returned, and the client has no other way to
 * tell "your purchase went through just now" from "your purchase had already
 * gone through". The vocabulary is the platform's own — the force-close seam
 * already reports `{refunded, nothingOwed, replayed}` (T-384).
 *
 * Everything else a success will carry (which item, what it cost, the resulting
 * balance) is deliberately **not** frozen here, and the asymmetry is Dex's own
 * reasoning applied honestly: a **request** field cannot be added later without a
 * wire change on a money path, while a **response** field can — a client
 * compiled against this schema keeps working when the answer grows. Freezing
 * fields nobody has designed yet would be inventing a shape, not preserving one.
 */
export const shopPurchaseSuccessSchema = z.object({
    /**
     * `true` when this response is the replay of an earlier, already-committed
     * request bearing the same nonce — never an error, never a second charge.
     * `false` (the default) for a purchase that committed on this request.
     */
    replayed: z.boolean().default(false),
});
/* ==========================================================================
   The two read shapes — A121 Item 3, T-401 / E4 `SHOP-API-READ`.
   ========================================================================== */
/**
 * One catalog row, as the storefront receives it.
 *
 * ── What is NOT here, and why each absence is deliberate ────────────────────
 *
 * - **`status`.** `draft` is an operator lifecycle word and Ivy §3.2 is LOCKED
 *   that it *never reaches a player string*. It is also the fact a client would
 *   need to derive sellability, which A121 Item 3 forbids: `purchasable` is
 *   server-owned and single-sourced. Not sending it makes the derivation
 *   impossible rather than merely discouraged.
 * - **`maxPerPlayer`, `payload`, `createdAt`, `sortOrder`.** Nothing on Ivy's
 *   card or dialog renders them. A read that names only what its consumer uses
 *   is the same discipline rule 42(d) applies to SQL, carried one hop further
 *   out — a field on the wire is a field somebody eventually derives from.
 *   `sort_order` **is** read: it orders the array (see {@link shopCatalogSchema}).
 * - **`shopEnabled`.** It already reaches the client in the lobby payload
 *   (`contracts/lobby.ts` → `lobbyMetaSchema`); a second copy here would be two
 *   sources for one flag (A81). When the shop is closed this endpoint carries no
 *   catalog at all — it refuses (`shop_unavailable`, 409).
 */
export const shopCatalogItemSchema = z.object({
    /** The purchase seam's `itemId`. Stable across a `sku` correction. */
    id: z.string().uuid(),
    /** The operator/seed handle, and the asset's slug (Ivy §7.5 "the sku is the slug"). */
    sku: z.string(),
    /**
     * **A plain string, never an enum — and that is load-bearing.**
     *
     * The same ruling `KNOWN_COLOR_TOKENS` carries in `lobby.ts`: an operator
     * adding a value the client does not know must produce the documented
     * fallback, *not a 500 from response serialisation*. A `z.enum` here would
     * turn A121's "an unknown category must surface" into "an unknown category
     * takes the whole catalog down", which is the failure the D2 asymmetry buys
     * and the reason it is cheap to close at the first read.
     */
    category: z.string(),
    /** Database content, rendered as-is. NOT an i18n key (Ivy §8.7). */
    name: z.string(),
    /** Database content. `null` is a row with no description, not an empty one. */
    description: z.string().nullable(),
    /**
     * **`null` for every row in the scaffold era.** Ivy §2.1: `null` renders the
     * `shop.price.unset` chip in the same 28px zone a number would occupy — never
     * `0`, which is a lie, and never absent, which is a layout change later.
     */
    priceCoins: z.number().int().nullable(),
    /**
     * **Server-owned, single-sourced** (A121 Item 3). `false` for every item in
     * the scaffold. The client never derives this from price, status or balance —
     * and is not sent the inputs it would need to try.
     */
    purchasable: z.boolean(),
    /**
     * A **fully resolved, servable URL**, or `null` when the row carries no asset
     * (or one that does not satisfy the §7.5 contract — see the resolver).
     *
     * The row stores a path relative to the art root and the api resolves it once
     * (Ivy's 2026-08-15 §7.5 addendum, LOCKED): *"the client never prepends a
     * prefix, never concatenates a path and never learns the on-disk filename"*.
     * `null` falls back to the category placeholder, silently — §7.6, not an
     * error state.
     */
    assetUrl: z.string().nullable(),
    /**
     * **The unknown-category marker.** `true` when the server did not recognise
     * `category`, i.e. the client is not expected to have a preview branch for it
     * and should draw the generic `?` placeholder (Ivy §7.6).
     *
     * It is a server statement, not a client derivation, for the same reason
     * `purchasable` is: one evaluator, one answer, and a log line beside it naming
     * the row an operator should fix.
     */
    previewFallback: z.boolean(),
});
/**
 * `GET /api/v1/shop/catalog`.
 *
 * **The array's ORDER IS PART OF THE CONTRACT.** Ivy §7.1 is LOCKED that
 * category section order derives from the **minimum `sort_order` within each
 * category**, server-owned, with *no client-side category enum ordering* (A81).
 * So the server ships the items in final render order — grouped into contiguous
 * runs by category, the runs ordered by each category's smallest `sortOrder`,
 * and items inside a run by `sortOrder` then `sku` — and the client groups
 * consecutive runs rather than sorting anything. A client that re-sorts this
 * array has invented a second ordering rule.
 */
export const shopCatalogSchema = z.object({
    items: z.array(shopCatalogItemSchema),
});
/**
 * `GET /api/v1/shop/inventory` — what the caller already owns.
 *
 * **Ids and nothing else.** The only thing Ivy's design does with ownership is
 * the card's owned pill (§7.3) and, at pricing time, the `already_owned`
 * refusal — which is decided on the server. `acquiredAt` renders nowhere, and
 * `txn_id` is a money-adjacent column the storefront must never read (A121 Item
 * 1); the read that produces this does not name either.
 *
 * Empty for every real player in the scaffold — nothing writes `player_inventory`
 * until `SHOP-DEBIT-PURCHASE`.
 */
export const shopInventorySchema = z.object({
    /** Ascending, so two calls to an unchanged inventory are byte-identical. */
    itemIds: z.array(z.string().uuid()),
});
//# sourceMappingURL=shop.js.map